import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './components/Home';
import Gift from './components/Gift';
import Portfolio from './components/Portfolio';
import CanHelp from './components/CanHelp';
import { useEffect } from 'react';
import projectsData from './data/projectsData';
import giftsData from './data/giftsData';
import { initAnalytics, trackPageView } from './analytics';

const siteUrl = 'https://hiredalmo.com';

function slugify(value = '') {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function absoluteUrl(path = '/') {
  return new URL(path, siteUrl).toString();
}

function upsertMeta(selector, createAttrs, valueAttr, value) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttrs).forEach(([key, attrValue]) => element.setAttribute(key, attrValue));
    document.head.appendChild(element);
  }
  element.setAttribute(valueAttr, value);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function getRouteMetadata(pathname) {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  const [, section, slug] = cleanPath.split('/');

  if (section === 'portfolio' && slug) {
    const project = projectsData.find(item => slugify(item.title) === slug);
    if (project) {
      return {
        title: `${project.title} - Dalmo Mendonca Portfolio`,
        description: project.description,
        path: `/portfolio/${slug}`,
        image: project.icon?.replace(/^\.\.\//, '/') || '/images/dalmo.jpg'
      };
    }
  }

  if (section === 'gift' && slug) {
    const gift = giftsData.find(item => slugify(item.title) === slug);
    if (gift) {
      return {
        title: `${gift.title} - Gift from Dalmo`,
        description: gift.summary,
        path: `/gift/${slug}`,
        image: gift.icon?.replace(/^\.\.\//, '/') || '/images/dalmo.jpg'
      };
    }
  }

  const metadataByPath = {
    '/': {
      title: 'Dalmo Mendonca - Healthcare IT and AI Consultant',
      description: 'Professional portfolio of Dalmo Mendonca, a healthcare IT and AI expert with deep Epic experience, generative AI skill, and a record of building practical tools.',
      path: '/',
      image: '/images/dalmo.jpg'
    },
    '/portfolio': {
      title: 'Projects - Dalmo Mendonca Portfolio',
      description: 'A curated portfolio of Dalmo Mendonca projects across healthcare IT, AI tools, web design, games, writing, integral practice, and creative technology.',
      path: '/portfolio',
      image: '/images/website-design.png'
    },
    '/gift': {
      title: 'Free Creative Work - Dalmo Mendonca',
      description: 'Free creative gifts from Dalmo Mendonca, including guided meditations, AI art, writing, teaching resources, and contemplative projects.',
      path: '/gift',
      image: '/images/ai-art.png'
    },
    '/help': {
      title: 'Can Dalmo Help? - Healthcare IT and AI Consulting Fit Check',
      description: 'Use an AI-assisted fit check to see whether Dalmo Mendonca can help with your healthcare IT, AI, Epic, writing, or creative technology project.',
      path: '/help',
      image: '/images/healthcare-it.png'
    }
  };

  return metadataByPath[cleanPath] || metadataByPath['/'];
}

function RouteInstrumentation() {
  const location = useLocation();

  useEffect(() => {
    const metadata = getRouteMetadata(location.pathname);
    const canonical = absoluteUrl(metadata.path);
    const image = absoluteUrl(metadata.image);

    document.title = metadata.title;
    upsertLink('canonical', canonical);
    upsertMeta('meta[name="description"]', { name: 'description' }, 'content', metadata.description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, 'content', canonical);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, 'content', metadata.title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, 'content', metadata.description);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, 'content', image);
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url' }, 'content', canonical);
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', metadata.title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', metadata.description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', image);

    trackPageView({
      path: metadata.path,
      title: metadata.title,
      location: canonical,
      siteName: 'hiredalmo'
    });
  }, [location.pathname]);

  return null;
}

initAnalytics('hiredalmo');

function App() {
  useEffect(() => {
    // Check if the ElevenLabs script is already added
    if (!document.querySelector('script[src="https://elevenlabs.io/convai-widget/index.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://elevenlabs.io/convai-widget/index.js';
      script.async = true;
      script.type = 'text/javascript';
      document.body.appendChild(script);
    }
  }, []);

  return (
    <Router>
      <div className="App min-h-screen flex flex-col">
        <RouteInstrumentation />
        <Navbar />
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:projectName" element={<Portfolio />} />
            <Route path="/gift" element={<Gift />} />
            <Route path="/gift/:giftName" element={<Gift />} />
            <Route path="/help" element={<CanHelp />} />
          </Routes>
        </div>
        {/* Add the widget globally */}
        <elevenlabs-convai agent-id="ZAiSfR2uFVDJoCPMp4uh"></elevenlabs-convai>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
